import React, { useEffect } from "react";
import Dryrose from "../images/corner/corner-dry-rose-art.webp";
import GoodKarma from "../images/good-karma.png";
import Button from "../components/Button";
import moment from "moment";
import { useHistory } from "react-router";
import StrapiImage from "../components/StrapiImage";
import FullPageLoader from "../components/FullPageLoader";

const getPlainText = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value.replace(/\s+/g, " ").trim();
  if (Array.isArray(value)) {
    return value
      .map((item) => getPlainText(item))
      .filter(Boolean)
      .join(" ")
      .replace(/\s+/g, " ")
      .trim();
  }
  if (typeof value === "object") {
    return [value.text, getPlainText(value.children)]
      .filter(Boolean)
      .join(" ")
      .replace(/\s+/g, " ")
      .trim();
  }
  return "";
};

const excerpt = (text, max = 140) => {
  if (!text) return "";
  if (text.length <= max) return text;
  return text.slice(0, max).replace(/\s+\S*$/, "") + "…";
};

const WineStories = (props) => {
  const history = useHistory();

  useEffect(() => {
    document.title = "Auburn Road - Wine Stories";
    return () => {
      document.title = "Auburn Road Vineyards";
    };
  }, []);

  // null/undefined means the Strapi request is still in flight
  if (props.stories === null || props.stories === undefined) {
    return <FullPageLoader />;
  }

  const getTitle = (story) =>
    story?.Title || story?.title || story?.fields?.title || "";
  const getUniqueUrl = (story) =>
    story?.unique_url || story?.uniqueUrl || story?.fields?.uniqueUrl || null;
  const getPublishDate = (story) =>
    story?.publish_date ||
    story?.publishDate ||
    story?.fields?.publishDate ||
    story?.createdAt ||
    story?.sys?.createdAt ||
    null;
  const getImageUrl = (story) => {
    let image = StrapiImage(story?.story_image);
    if (!image) return GoodKarma;
    if (typeof image === "string") return image || GoodKarma;
    if (image?.url) return image.url;
    if (image?.formats?.large?.url) return image.formats.large.url;
    if (image?.formats?.medium?.url) return image.formats.medium.url;
    if (image?.formats?.small?.url) return image.formats.small.url;
    if (image?.formats?.thumbnail?.url) return image.formats.thumbnail.url;
    if (image?.fields?.file?.url) return image.fields.file.url;
    return GoodKarma;
  };
  const getBlurb = (story) => {
    const title = getTitle(story);
    const blurb = getPlainText(
      story?.blurb || story?.attributes?.blurb || story?.fields?.blurb
    );
    if (blurb && blurb !== title) return excerpt(blurb);
    const content = getPlainText(
      story?.content || story?.attributes?.content || story?.fields?.content
    );
    if (content && content !== title) return excerpt(content);
    return blurb;
  };
  const getStoryPath = (story) => {
    const slug = (getTitle(story) || "").split(" ").join("-").toLowerCase();
    return "/" + (getUniqueUrl(story) || slug);
  };

  const sortedStories = (props.stories || []).slice().sort((a, b) => {
    return moment(getPublishDate(b)) - moment(getPublishDate(a));
  });
  const featured = sortedStories[0];
  const remaining = sortedStories.slice(1);

  const goToStory = (story) => history.push(getStoryPath(story));

  const renderCard = (story, { featuredCard = false, key } = {}) => {
    const image = getImageUrl(story);
    const blurb = getBlurb(story);
    const style = {
      "--bg-image": `url(${image})`,
    };
    return (
      <article
        key={key}
        className={`wine-story-wrap story-card${
          featuredCard ? " story-card--featured" : ""
        }`}
      >
        <div
          className="story-image pointer"
          style={style}
          bg-image={image}
          onClick={() => goToStory(story)}
          role="presentation"
        />
        <div className="story-card__copy">
          <span className="story-card__tags">
            {featuredCard && <span className="story-card__tag">Latest</span>}
            <span className="story-card__tag">
              {moment(getPublishDate(story)).format("MMMM Do, YYYY")}
            </span>
          </span>
          <h3
            className="story-card__title pointer"
            onClick={() => goToStory(story)}
          >
            {getTitle(story)}
          </h3>
          {blurb ? <p className="story-card__blurb blurb">{blurb}</p> : null}
          <Button
            onClick={() => goToStory(story)}
            className="continue-reading"
            small
            type="plain"
          >
            Continue reading
          </Button>
        </div>
      </article>
    );
  };

  return (
    <>
      <img className="corner-dry-rose" src={Dryrose} alt="" />
      <div className="container stories-page p-t-100">
        <section className="stories-hero">
          <p className="stories-hero__kicker">Auburn Road Vineyards</p>
          <h1 className="huge-text-no-quote mb-3">Wine Stories</h1>
          <p className="stories-hero__intro">
            Dispatches from the vineyard: harvest notes, Italy trips, and the
            long tale of making wine in New Jersey.
          </p>
        </section>

        {sortedStories.length ? (
          <>
            <div className="first-story">
              {renderCard(featured, { featuredCard: true })}
            </div>
            {remaining.length > 0 && (
              <div className="story-grid">
                {remaining.map((story, i) =>
                  renderCard(story, { key: "wine-story-" + i })
                )}
              </div>
            )}
          </>
        ) : null}
      </div>
    </>
  );
};

export default WineStories;
